import React, { useState, useEffect } from 'react';
import { postAPI, postAPIForm } from '../../../../app/api';
import editIcon from "../../../../theme/assets/images/icon/edit-icon.svg"
import deleteIcon from "../../../../theme/assets/images/icon/delete-icon.svg"
import Swal from 'sweetalert2';

export default function CampusToCareerSection() {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        title: 'From Campus to Career',
        subtitle: 'Alumni Speak',
        description: '',
        video_url: '',
        video_thumbnail: null,
        status: 1
    });

    // Fetch all records
    useEffect(() => {
        fetchRecords();
    }, []);

    const fetchRecords = async () => {
        try {
            setLoading(true);
            const response = await postAPI('campus-career/list', {});
            if (response?.status === 1) {
                setRecords(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching records:', error);
            Swal.fire('Error', 'Failed to fetch records', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle form changes
    const handleChange = (e) => {
        const { name, value, files, type, checked } = e.target;
        if (type === 'file') {
            setFormData(prev => ({
                ...prev,
                [name]: files && files.length > 0 ? files[0] : null
            }));
        } else if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked ? 1 : 0
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Function to convert YouTube URL to embed format for preview
    const getEmbedUrl = (url) => {
        if (!url) return "";
        
        // If already an embed URL, return as is
        if (url.includes('youtube.com/embed/') || url.includes('youtu.be/embed/')) {
            return url;
        }
        
        // Extract video ID from various YouTube URL formats
        let videoId = '';
        
        // Standard YouTube URL: https://www.youtube.com/watch?v=VIDEO_ID
        if (url.includes('youtube.com/watch?v=')) {
            videoId = url.split('v=')[1]?.split('&')[0];
        }
        // Shortened YouTube URL: https://youtu.be/VIDEO_ID
        else if (url.includes('youtu.be/')) {
            videoId = url.split('youtu.be/')[1]?.split('?')[0];
        }
        // Mobile YouTube URL: https://m.youtube.com/watch?v=VIDEO_ID
        else if (url.includes('m.youtube.com/watch?v=')) {
            videoId = url.split('v=')[1]?.split('&')[0];
        }
        
        // If video ID found, create embed URL
        if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;
        }
        
        // Return original URL if no conversion possible
        return url;
    };

    // Add/Update record
    const handleAddRecord = async (e) => {
        e.preventDefault();
        try {
            if (!formData.title || !formData.description || !formData.video_url) {
                Swal.fire('Validation', 'Please fill all required fields', 'warning');
                return;
            }

            let submitData = {
                title: formData.title,
                subtitle: formData.subtitle,
                description: formData.description,
                video_url: formData.video_url,
                status: formData.status
            };

            if (editingId) submitData.id = editingId;

            // Upload thumbnail if exists
            if (formData.video_thumbnail && typeof formData.video_thumbnail !== 'string') {
                const thumbFormData = new FormData();
                thumbFormData.append('baseDir', 'campus-career');
                thumbFormData.append('filename', 'video_thumbnail');
                thumbFormData.append('original_file', formData.video_thumbnail);

                const thumbResponse = await postAPIForm('file/uploadFile', thumbFormData);
                if (thumbResponse?.status === 1) {
                    submitData.video_thumbnail = thumbResponse?.filepath;
                }
            } else if (formData.video_thumbnail && typeof formData.video_thumbnail === 'string') {
                submitData.video_thumbnail = formData.video_thumbnail;
            }

            const response = await postAPI('campus-career/create', submitData);
            if (response?.status === 1) {
                Swal.fire('Success', 'Content saved successfully', 'success');
                resetForm();
                fetchRecords();
            } else {
                Swal.fire('Error', response?.msg || 'Failed to save content', 'error');
            }
        } catch (error) {
            console.error('Error saving record:', error);
            Swal.fire('Error', error.message || 'Failed to save content', 'error');
        }
    };

    // Delete record
    const handleDeleteRecord = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: 'Are you sure you want to delete this content?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('campus-career/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Content deleted successfully', 'success');
                    fetchRecords();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete content', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            title: 'From Campus to Career',
            subtitle: 'Alumni Speak',
            description: '',
            video_url: '',
            video_thumbnail: null,
            status: 1
        });
        setShowForm(false);
        setEditingId(null);
    };

    const handleToggleForm = () => {
        if (showForm) {
            setShowForm(false);
            resetForm();
        } else {
            setEditingId(null);
            setFormData({
                title: 'From Campus to Career',
                subtitle: 'Alumni Speak',
                description: '',
                video_url: '',
                video_thumbnail: null,
                status: 1
            });
            setShowForm(true);
        }
    };

    const handleEditRecord = (item) => {
        setFormData({
            title: item.title || 'From Campus to Career',
            subtitle: item.subtitle || 'Alumni Speak',
            description: item.description || '',
            video_url: item.video_url || '',
            video_thumbnail: item.video_thumbnail || null,
            status: item.status || 1
        });
        setEditingId(item.id);
        setShowForm(true);
        // Open the modal
        setTimeout(() => {
            document.getElementById("openCareerModalBtn").click();
        }, 0);
    };

    return (
        <>
            <div className="container-fluid">
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <h3 className="mb-0">Campus to Career Section</h3>
                            {/* <button 
                                className="btn btn-primary"
                                onClick={handleToggleForm}
                            >
                                {showForm ? 'Cancel' : 'Add New Content'}
                            </button> */}
                            <button className="btn btn-primary"
                                onClick={() => {
                                    resetForm();
                                    document.getElementById("openCareerModalBtn").click();
                                }}
                            >{showForm ? 'Cancel' : 'Add New Content'}</button>
                        </div>
                    </div>
                </div>
                <button
                    id="openCareerModalBtn"
                    type="button"
                    className="d-none"
                    data-bs-toggle="modal"
                    data-bs-target="#careerModal"
                ></button>
                {/* {showForm && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5 className="card-title">{editingId ? 'Edit Content' : 'Add New Content'}</h5>
                            <form onSubmit={handleAddRecord}>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Title *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleChange}
                                            placeholder="e.g., From Campus to Career"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Subtitle</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="subtitle"
                                            value={formData.subtitle}
                                            onChange={handleChange}
                                            placeholder="e.g., Alumni Speak"
                                        />
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label">Description *</label>
                                        <textarea
                                            className="form-control"
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            rows="4"
                                            placeholder="Enter description"
                                            required
                                        ></textarea>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Video URL *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="video_url"
                                            value={formData.video_url}
                                            onChange={handleChange}
                                            placeholder="e.g., https://youtube.com/embed/..."
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Video Thumbnail</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            name="video_thumbnail"
                                            onChange={handleChange}
                                            accept="image/*"
                                        />
                                        {formData.video_thumbnail && typeof formData.video_thumbnail === 'string' && (
                                            <small className="text-muted">Current: {formData.video_thumbnail}</small>
                                        )}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-control"
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                        >
                                            <option value={1}>Active</option>
                                            <option value={0}>Inactive</option>
                                        </select>
                                    </div>
                                </div>
                                <button type="submit" className="btn btn-success">
                                    {editingId ? 'Update' : 'Add'} Content
                                </button>
                            </form>
                        </div>
                    </div>
                )} */}
                <div className="modal fade modal-white" id="careerModal" tabIndex="-1" aria-hidden="true">
                    <div className="modal-dialog modal-md modal-dialog-centered">
                        <div className="modal-content">

                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingId ? "Edit Content" : "Add New Content"}
                                </h5>
                                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                            </div>

                            <div className="modal-body">

                                <form onSubmit={handleAddRecord}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Title *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleChange}
                                                placeholder="e.g., From Campus to Career"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Subtitle</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="subtitle"
                                                value={formData.subtitle}
                                                onChange={handleChange}
                                                placeholder="e.g., Alumni Speak"
                                            />
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label">Description *</label>
                                            <textarea
                                                className="form-control"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                rows="4"
                                                placeholder="Enter description"
                                                required
                                            ></textarea>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Video URL *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="video_url"
                                                value={formData.video_url}
                                                onChange={handleChange}
                                                placeholder="e.g., https://www.youtube.com/watch?v=abc123 or https://youtu.be/abc123"
                                                required
                                            />
                                            <small className="text-muted">
                                                Supports: YouTube watch URLs, youtu.be links, and embed URLs. 
                                                The system will automatically convert them to the correct format.
                                            </small>
                                            {formData.video_url && (
                                                <div className="mt-2">
                                                    <small className="text-success">
                                                        Preview URL: {getEmbedUrl(formData.video_url)}
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Video Thumbnail</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                name="video_thumbnail"
                                                onChange={handleChange}
                                                accept="image/*"
                                            />
                                            {formData.video_thumbnail && typeof formData.video_thumbnail === 'string' && (
                                                <small className="text-muted">Current: {formData.video_thumbnail}</small>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-control"
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                            >
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>
                                    </div>

                                    <button type="submit" className="btn btn-success">
                                        {editingId ? 'Update' : 'Add'} Content
                                    </button>
                                </form>
                            </div>

                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-striped table-hover cus-table">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Title</th>
                                            <th>Subtitle</th>
                                            <th>Description</th>
                                            <th>Video URL</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {records && records.length > 0 ? (
                                            records.map((item, index) => (
                                                <tr key={index}>
                                                    <td><strong>{item.title}</strong></td>
                                                    <td>{item.subtitle}</td>
                                                    <td>{item.description?.substring(0, 40)}...</td>
                                                    <td><a href={item.video_url} target="_blank" rel="noopener noreferrer">{item.video_url?.substring(0, 30)}...</a></td>
                                                    <td>
                                                        <span className={`badge badge-${item.status === 1 ? 'success' : 'danger'}`}>
                                                            {item.status === 1 ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn me-2 py-0 my-0 mx-0 "
                                                            onClick={() => handleEditRecord(item)}
                                                        >
                                                            <img src={editIcon} />
                                                        </button>
                                                        <button
                                                            className="btn py-0 my-0 mx-0"
                                                            onClick={() => handleDeleteRecord(item.id)}
                                                        >
                                                            <img src={deleteIcon} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4">
                                                    No content found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}